import React from "react";
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthProvider';
import './styles/layout.css';
import ProtectedRoute from './components/PrivateRoute';
import ModuleGuard from './components/ModuleGuard';
import LoginFormTab from './components/masterdata/LoginFormTab';
import Loading from './components/Loading';
import SchemeCategory from './pages/masterdata/SchemeCategory';
import Standard from './pages/masterdata/Standard';
import IsccMaterialCategory from './pages/masterdata/IsccMaterialCategory';
import IsccMaterial from './pages/masterdata/IsccMaterial';
import LvvScheme from './pages/masterdata/LvvScheme';
import LvvNek from './pages/masterdata/LvvNek';
import ReasonLoss from './pages/masterdata/ReasonLoss';
import Branch from './pages/masterdata/Branch';
import Tagging from './pages/masterdata/Tagging';
import Competitor from './pages/masterdata/Competitor';
import Services from './pages/masterdata/Services';
import AuditType from './pages/masterdata/AuditType';
import Competency from './pages/masterdata/Competency';
import Impartiality from './pages/masterdata/Impartiality';
import CompanyType from './pages/masterdata/CompanyType';
import Sector from './pages/masterdata/Sector';
import Source from './pages/masterdata/Source';
import Departement from './pages/masterdata/Departement';
import JobTittle from './pages/masterdata/JobTittle';
import JobTitlePermission from './pages/masterdata/JobTitlePermission';
import Contacts from './pages/masterdata/Contacts';
import AddressAutoComplete from './components/addressAutoComplete/AddressAutoComplete';
import NaceCode from './pages/masterdata/NaceCode'
import Status from './pages/masterdata/Status';
import ProposalStatus from './pages/masterdata/ProposalStatus';
import ProposalStage from './pages/masterdata/ProposalStage';
import BusinessPartnerType from './pages/masterdata/BusinessPartnerType';
import IndividualPartnerType from './pages/masterdata/IndividualPartnerType';
import IndividualPartner from './pages/masterdata/IndividualPartner';
import EntityType from './pages/masterdata/EntityType';
import TaskType from './pages/masterdata/TaskType';
import Companies from './pages/masterdata/Companies';
import CompanyDetail from './pages/masterdata/CompanyDetail';
import StandardDetail from './pages/masterdata/StandardDetail'
import IsccScope from './pages/masterdata/IsccScope';
import LeadsStatus from './pages/masterdata/LeadsStatus';
import ActivityList from './pages/masterdata/ActivityList';
import ProductLevel from './pages/masterdata/ProductLevel';
import ProductCategory from './pages/masterdata/ProductCategory';
import BusinessPartner from './pages/masterdata/BusinessPartner';
import Dashboard from './pages/masterdata/Dashboard';
import ListUsers from './pages/masterdata/ListUsers';

import PersonnelQualification from './pages/masterdata/PersonnelQualification';
import CommercialTerms from './pages/masterdata/CommercialTerms';
import CommercialTermDetail from './pages/masterdata/CommercialTermDetail';
import Workflow from './pages/masterdata/Workflow';
import FindingCategory from './pages/masterdata/FindingCategory';
import PublicHoliday from './pages/masterdata/PublicHoliday';

// Disable console logs in production builds only
// Use REACT_APP_ENV=staging in .env.staging to control staging behavior separately
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.warn = () => {};
  console.info = () => {};
  console.debug = () => {};
  // NOTE: console.error intentionally kept active in production for error monitoring
}

// Authentication-related pages don't need the layout
const AuthLayout = ({ children }) => (
  <div className="page">
    {children}
  </div>
);

function App() {
  const { loading, isAuthenticated } = useAuth();

  // Show loading while checking authentication
  if (loading) {
    return <Loading />;
  }

  return (
    <Routes>
      {/* Login route */}
      <Route path="/masterdata" element={
        isAuthenticated() ? <Navigate to="/dashboard" replace /> : <AuthLayout><LoginFormTab /></AuthLayout>
      } />
      
      {/* Default redirect to login */}
      <Route path="/" element={<Navigate to="/masterdata" replace />} />

      {/* Main layout routes, protected */}
      <Route element={<ProtectedRoute />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/masterdata-activity-list" element={<ModuleGuard moduleKey="activityList"><ActivityList /></ModuleGuard>} />
        {/* <Route path="/masterdata-business-process" element={<BusinessProcess />} /> */}
        <Route path="/masterdata-company" element={<ModuleGuard moduleKey="companies"><Companies /></ModuleGuard>} />
        <Route path="/masterdata-scheme-category" element={<ModuleGuard moduleKey="schemeCategory"><SchemeCategory /></ModuleGuard>} />
        <Route path="/masterdata-standard" element={<ModuleGuard moduleKey="standard"><Standard /></ModuleGuard>} />
        <Route path="/masterdata-isccscope" element={<ModuleGuard moduleKey="isccScope"><IsccScope /></ModuleGuard>} />
        <Route path="/masterdata-isccmaterialcategory" element={<ModuleGuard moduleKey="isccMaterialCategory"><IsccMaterialCategory /></ModuleGuard>} />
        <Route path="/masterdata-isccmaterial" element={<ModuleGuard moduleKey="isccMaterial"><IsccMaterial /></ModuleGuard>} />
        <Route path="/masterdata-lvvscheme" element={<ModuleGuard moduleKey="lvvScheme"><LvvScheme /></ModuleGuard>} />
        <Route path="/masterdata-lvvnek" element={<ModuleGuard moduleKey="lvvNek"><LvvNek /></ModuleGuard>} />
        <Route path="/masterdata-nacecode" element={<ModuleGuard moduleKey="naceCode"><NaceCode /></ModuleGuard>} />
        <Route path="/masterdata-services" element={<ModuleGuard moduleKey="services"><Services /></ModuleGuard>} />
        <Route path="/masterdata-audittype" element={<ModuleGuard moduleKey="auditType"><AuditType /></ModuleGuard>} />
        <Route path="/masterdata-audit-competency" element={<ModuleGuard moduleKey="competency"><Competency /></ModuleGuard>} />
        <Route path="/masterdata-audit-impartiality" element={<ModuleGuard moduleKey="impartiality"><Impartiality /></ModuleGuard>} />
        <Route path="/masterdata-audit-workflow" element={<ModuleGuard moduleKey="workflow"><Workflow /></ModuleGuard>} />
        <Route path="/masterdata-finding-category" element={<FindingCategory />} />
        <Route path="/masterdata-public-holiday" element={<PublicHoliday />} />
        <Route path="/masterdata-companytype" element={<ModuleGuard moduleKey="companyType"><CompanyType /></ModuleGuard>} />
        <Route path="/masterdata-sector" element={<ModuleGuard moduleKey="sector"><Sector /></ModuleGuard>} />
        <Route path="/masterdata-source" element={<ModuleGuard moduleKey="source"><Source /></ModuleGuard>} />
        <Route path="/masterdata-status" element={<ModuleGuard moduleKey="status"><Status /></ModuleGuard>} />
        <Route path="/masterdata-lead-status" element={<ModuleGuard moduleKey="leadsStatus"><LeadsStatus /></ModuleGuard>} />
        <Route path="/masterdata-proposal-status" element={<ModuleGuard moduleKey="proposalStatus"><ProposalStatus /></ModuleGuard>} />
        <Route path="/masterdata-proposal-stage" element={<ModuleGuard moduleKey="proposalStage"><ProposalStage /></ModuleGuard>} />
        <Route path="/masterdata-reason-loss" element={<ModuleGuard moduleKey="reasonLoss"><ReasonLoss /></ModuleGuard>} />
        <Route path="/masterdata-branch" element={<ModuleGuard moduleKey="branches"><Branch /></ModuleGuard>} />
        <Route path="/masterdata-tagging" element={<ModuleGuard moduleKey="tagging"><Tagging /></ModuleGuard>} />
        <Route path="/masterdata-competitor" element={<ModuleGuard moduleKey="competitor"><Competitor /></ModuleGuard>} />
        <Route path="/masterdata-departement" element={<ModuleGuard moduleKey="department"><Departement /></ModuleGuard>} />
        <Route path="/masterdata-jobtitle" element={<ModuleGuard moduleKey="jobTitle"><JobTittle /></ModuleGuard>} />
        <Route path="/masterdata-jobtitle/:id" element={<ModuleGuard moduleKey="jobTitle"><JobTitlePermission /></ModuleGuard>} />
        <Route path="/masterdata-contacts" element={<ModuleGuard moduleKey="contacts"><Contacts /></ModuleGuard>} />
        <Route path="/masterdata-entity-type" element={<ModuleGuard moduleKey="entityType"><EntityType /></ModuleGuard>} />
        <Route path="/masterdata-task-type" element={<ModuleGuard moduleKey="taskType"><TaskType /></ModuleGuard>} />
        <Route path="/masterdata-list-users" element={<ModuleGuard moduleKey="listUsers"><ListUsers /></ModuleGuard>} />
        <Route path="/masterdata-autocomplete" element={<AddressAutoComplete />} />
        <Route path="/masterdata-business-partner-type" element={<ModuleGuard moduleKey="businessPartnerType"><BusinessPartnerType /></ModuleGuard>} />
        <Route path="/masterdata-individual-partner-type" element={<ModuleGuard moduleKey="individualPartnerType"><IndividualPartnerType /></ModuleGuard>} />
        <Route path="/masterdata-individual-partner" element={<ModuleGuard moduleKey="individualPartner"><IndividualPartner /></ModuleGuard>} />
        <Route path="/companies/:id" element={<CompanyDetail />} />
        <Route path="/standards/:id" element={<StandardDetail />} />
        <Route path="/masterdata-product-level" element={<ModuleGuard moduleKey="productLevel"><ProductLevel /></ModuleGuard>} />
        <Route path="/masterdata-product-category" element={<ModuleGuard moduleKey="productCategory"><ProductCategory /></ModuleGuard>} />
        <Route path="/masterdata-business-partner" element={<ModuleGuard moduleKey="businessPartner"><BusinessPartner /></ModuleGuard>} />
        <Route path="/masterdata-personnel-qualification" element={<ModuleGuard moduleKey="personnelQualification"><PersonnelQualification /></ModuleGuard>} />
        <Route path="/masterdata-commercial-terms" element={<ModuleGuard moduleKey="commercialTerms"><CommercialTerms /></ModuleGuard>} />
        <Route path="/masterdata-commercial-terms/:id" element={<ModuleGuard moduleKey="commercialTermDetail"><CommercialTermDetail /></ModuleGuard>} />
      </Route>

      {/* Default redirect */}
      <Route path="*" element={
        isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Navigate to="/masterdata" replace />
      } />
    </Routes>
  );
}

export default App;
